import React from 'react';
import Relay from 'react-relay';
import { defineMessages, FormattedMessage } from 'react-intl';

const messages = defineMessages({
    loadingRuns: {
        id: 'snapshotTesting.loadingRuns',
        defaultMessage: 'Loading runs...',
    },
    loadRunsFailed: {
        id: 'snapshotTesting.loadRunsFailed',
        defaultMessage: 'Failed to load runs. Collapse and expand the row to try again.',
    },
});

export interface TestRunHistoryRun {
    id?: any;
    realId?: string | null;
    status?: string;
    duration?: number;
    createdAt?: any;
    error?: string | null;
}

interface TestRunHistoryProps {
    testInvocation: { runs?: { edges?: Array<{ node: TestRunHistoryRun }> } | null } | null;
    relay: {
        variables: { expanded?: boolean };
        setVariables: (variables: { expanded: boolean }, onReadyStateChange?: (readyState: any) => void) => void;
        forceFetch: (variables?: any, onReadyStateChange?: (readyState: any) => void) => void;
    };
    renderRuns: (runs: TestRunHistoryRun[]) => React.ReactNode;
    refreshToken?: number;
}

interface TestRunHistoryState {
    loadFailed: boolean;
}

/**
 * Run history for one expanded result row (QA-10 F20). The history connection is fetched only
 * after the row is expanded (this component mounts), instead of eagerly for every listed testcase.
 */
export class TestRunHistory extends React.Component<TestRunHistoryProps, TestRunHistoryState> {
    constructor(props: TestRunHistoryProps) {
        super(props);
        this.state = { loadFailed: false };
    }

    componentDidMount() {
        if (!this.props.relay.variables.expanded) {
            this.props.relay.setVariables({ expanded: true }, this.handleReadyStateChange);
        }
    }

    componentWillReceiveProps(nextProps: TestRunHistoryProps) {
        if (nextProps.refreshToken !== this.props.refreshToken && this.props.relay.variables.expanded) {
            this.props.relay.forceFetch(undefined, this.handleReadyStateChange);
        }
    }

    handleReadyStateChange = (readyState: any) => {
        if (readyState && readyState.error) {
            this.setState({ loadFailed: true });
        } else if (readyState && readyState.done && this.state.loadFailed) {
            this.setState({ loadFailed: false });
        }
    };

    render() {
        const { testInvocation, relay, renderRuns } = this.props;
        const connection = testInvocation && testInvocation.runs;
        if (relay.variables.expanded && connection) {
            return renderRuns(connection.edges ? connection.edges.map(edge => edge.node) : []);
        }
        if (this.state.loadFailed) {
            return (
                <p className="text-danger" role="alert">
                    <FormattedMessage {...messages.loadRunsFailed} />
                </p>
            );
        }
        return (
            <p className="runs-loading" role="status">
                <i aria-hidden="true" className="fa fa-spinner fa-spin" /> <FormattedMessage {...messages.loadingRuns} />
            </p>
        );
    }
}

export default Relay.createContainer(TestRunHistory, {
    initialVariables: {
        expanded: false,
    },
    fragments: {
        testInvocation: () => Relay.QL`
            fragment on TestInvocation {
                runs(first: 50) @include(if: $expanded) {
                    edges {
                        node {
                            id
                            realId
                            status
                            duration
                            createdAt
                            error
                        }
                    }
                }
            }
        `,
    },
});
